import { defineStore } from 'pinia'
import type { RouteLocationNamedRaw, RouteRecordName } from 'vue-router'
import type { z } from 'zod'
import { RouterName } from '@/enums/router-name'
import { routerSettingSchema } from '@/models/router-setting'
import { useStorage } from '~library/composables'
import { ErrorCode, LocalStorage } from '~library/enums'
import { isType } from '~library/utils/functions'

const partialRouterSettingSchema = routerSettingSchema.partial()

type StorageRouterData = z.infer<typeof partialRouterSettingSchema>

export const useRouterStorageStore = defineStore('routerStorage', () => {
  const _storageRouterData = ref<StorageRouterData | null>(null)
  const cacheRouter = ref<RouteRecordName[]>([
    RouterName.JOURNEY_HOSPITALITY_ARRANGEMENT_DETAIL
  ])

  const route = useRoute()
  const router = useRouter()
  const { getStorageData, setStorageData } = useStorage()

  const storageRouterData = computed((): StorageRouterData | null => {
    return _storageRouterData.value
  })

  const setStorageRouterData = (data: StorageRouterData | null) => {
    if (data) {
      _storageRouterData.value = data
    } else {
      _storageRouterData.value = null
    }
  }

  const cacheRouterData = (
    data: RouteLocationNamedRaw
  ): RouteLocationNamedRaw => {
    const { name, params, query, ...otherRouterData } = data

    if (name && cacheRouter.value.includes(name)) {
      setStorageRouterData({
        ...storageRouterData.value,
        [name]: {
          query,
          params
        }
      })

      return {
        name,
        ...otherRouterData
      }
    }

    return data
  }

  const navigationType = (
    window.performance.getEntriesByType(
      'navigation'
    )[0] as PerformanceNavigationTiming
  ).type

  const initStorageRouterData = () => {
    // 重整或返回上一頁時，將路由暫存資料從 localStorage 取出來
    if (navigationType === 'reload' || navigationType === 'back_forward') {
      _storageRouterData.value =
        getStorageData({
          key: LocalStorage.ROUTER_DATA,
          zodSchema: partialRouterSettingSchema,
          autoRemove: true
        }) ?? null
    }

    checkDataIsValid()
  }

  /** 檢查路由資料時否正確，不正確時導向 404 頁面 */
  const checkDataIsValid = () => {
    const routeName = route.name

    if (
      routeName &&
      cacheRouter.value.includes(routeName) &&
      isType(routeName, routerSettingSchema.keyof()) &&
      !isType(
        _storageRouterData.value?.[routeName],
        routerSettingSchema.shape[routeName],
        true
      )
    ) {
      router.replace({
        name: RouterName.ERROR,
        params: {
          errorCode: ErrorCode.NOT_FOUND
        }
      })
    }
  }

  window.onbeforeunload = function () {
    if (storageRouterData.value) {
      setStorageData(LocalStorage.ROUTER_DATA, storageRouterData.value)
    }
  }

  watch(() => route.name, initStorageRouterData, {
    once: true
  })

  return {
    cacheRouter,
    storageRouterData,
    setStorageRouterData,
    cacheRouterData,
    initStorageRouterData
  }
})
